import { memo, forwardRef, useImperativeHandle, useRef, useMemo, useState, useContext } from 'react';
import { ThumbSvg, ThumbedSvg, TreadSvg, TreadedSvg, FeedbackSvg, CopySvg } from '@/sub-luca-plugin/compatible-components/svg-icons';
import { copyToClipboard, isWeApp } from '@/sub-luca-plugin/utils';
import { useControlActionBarVisible } from './helper';
import { eventBusExample } from '@/sub-luca-plugin/utils/class-example';
import { TFeedbackType } from '@/sub-luca-plugin/types';
import { feedback_sumbit } from '@/sub-luca-plugin/apis/qa';
import { QaRecordsContext } from '../../../helper';
import classNames from 'classnames';
import { tCommon } from '@/sub-luca-plugin/i18n';
import './index.scss';

export interface IActionBarRef {
    show: () => void;
    hide: () => void;
    control: () => void;
}

interface IProps {
    recordId: string;
    content: string;
    /**
     * 是否常驻
     */
    isPermanent?: boolean;
    feedbackType?: 1 | 2;
    isFeedbackEvaluation?: boolean;
}

const ActionBar = ({ recordId, isPermanent, content, feedbackType, isFeedbackEvaluation }: IProps, ref: any) => {
    const { getQaStoreByContext, toastInQa } = useContext(QaRecordsContext);
    const [curFeedbackType, setCurFeedbackType] = useState(feedbackType);
    const [curIsFeedbackEvaluation, setCurIsFeedbackEvaluation] = useState(isFeedbackEvaluation);
    const [actionBarVisible, setActionBarVisible] = useControlActionBarVisible();
    const timer = useRef<ReturnType<typeof setTimeout>>();

    const visible = useMemo(() => {
        if (isPermanent) return true;
        return actionBarVisible;
    }, [actionBarVisible, isPermanent]);

    const handleThumbOrTread = async (feedbackType: TFeedbackType) => {
        setCurFeedbackType(feedbackType);
        const { qaConfig } = getQaStoreByContext()!;
        feedback_sumbit({ lucaAskChatId: qaConfig.lucaAskChatId!, lucaAskRecordId: recordId, feedbackType });
    };

    const handleShow = () => {
        if (isPermanent) return;
        clearTimeout(timer.current);
        setActionBarVisible(true);
    };

    const handleHide = (immediately = false) => {
        if (isPermanent) return;
        if (immediately) {
            setActionBarVisible(false);
        } else {
            timer.current = setTimeout(() => setActionBarVisible(false), 100);
        }
    };

    const handleControl = () => {
        setActionBarVisible(!actionBarVisible);
    };

    const handleThumb = () => {
        handleHide(true);
        handleThumbOrTread(1);
    };

    const handleTread = () => {
        handleHide(true);
        handleThumbOrTread(2);
    };

    const handleOpenFeedbackModal = () => {
        handleHide(true);
        eventBusExample.publish('FeedbackModal', {
            type: 'open',
            params: { recordId },
            callbacks: {
                success: () => setCurIsFeedbackEvaluation(true),
            },
        });
    };

    const handleCopy = () => {
        handleHide(true);
        // 微信小程序中，不需要弹出提示，复制方法自带提示
        !isWeApp && toastInQa?.(tCommon.t('copySuccess', '复制成功'), 'success');
        copyToClipboard(content);
    };

    useImperativeHandle(
        ref,
        () => ({
            show: handleShow,
            hide: handleHide,
            control: handleControl,
        }),
        [isPermanent, actionBarVisible]
    );

    return visible ? (
        <div
            className={classNames('action-bar', isPermanent && 'action-bar--permanent')}
            onMouseEnter={handleShow}
            onMouseLeave={() => handleHide()}
        >
            {curFeedbackType === undefined && (
                <>
                    <ThumbSvg className="action-bar__thumb" dataText={tCommon.t('thumb', '赞')} onClick={handleThumb} />
                    <div className="action-bar__line" />
                    <TreadSvg className="action-bar__tread" dataText={tCommon.t('tread', '踩')} onClick={handleTread} />
                </>
            )}
            {curFeedbackType === 1 && <ThumbedSvg />}
            {curFeedbackType === 2 && <TreadedSvg />}
            <div className="action-bar__line" />
            {!curIsFeedbackEvaluation && (
                <>
                    <FeedbackSvg
                        className="action-bar__feedback"
                        dataText={tCommon.t('feedback', '反馈')}
                        onClick={handleOpenFeedbackModal}
                    />
                    <div className="action-bar__line" />
                </>
            )}
            <CopySvg className="action-bar__copy" dataText={tCommon.t('copy', '复制')} onClick={handleCopy} />
        </div>
    ) : null;
};

export default memo(forwardRef(ActionBar));
